/**
 * Pictures and video, large: the dark layer the chat's image lightbox uses. A set
 * opens as a collage, each tile opens on its own, and Escape steps back out.
 */
import { useCallback, useEffect, useRef, useState, type FC, type RefObject } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, Download, Play, X } from 'lucide-react';
import { downloadWorkspaceFile, useFileUrl } from './fileMedia';
import { fileKind, fileName } from './fileRefs';
import type { ViewerFile } from './FileViewer';

const CONTROL = 'flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white transition-colors hover:bg-white/20';

const nameOf = (file: ViewerFile) => file.name ?? fileName(file.path);
const isVideo = (file: ViewerFile) => fileKind(nameOf(file), file.contentType) === 'video';

/** "12 images", "3 videos", "4 files". */
export function setLabel(files: ViewerFile[]): string {
  const videos = files.filter(isVideo).length;
  return `${files.length} ${videos === files.length ? 'videos' : videos === 0 ? 'images' : 'files'}`;
}

/** Two columns up to four tiles, then wider as the set grows, so a small set stays large. */
function collageLayout(n: number): string {
  if (n <= 4) return 'columns-2 max-w-3xl';
  if (n <= 9) return 'columns-2 sm:columns-3 max-w-5xl';
  return 'columns-2 sm:columns-3 lg:columns-4 max-w-6xl';
}

function CollageTile({ threadId, file, onOpen }: { threadId: string; file: ViewerFile; onOpen: () => void }) {
  const { ref, url, failed, onError } = useFileUrl(threadId, file.path);
  const name = nameOf(file);
  return (
    <div ref={ref as RefObject<HTMLDivElement>} className="group relative mb-2 break-inside-avoid overflow-hidden rounded-md bg-white/5">
      <button type="button" onClick={onOpen} title={file.path} className="block w-full cursor-zoom-in">
        {failed ? (
          <div className="flex aspect-square items-center justify-center px-3 text-center text-xs text-white/50">No longer in the workspace</div>
        ) : !url ? (
          <div className="aspect-square animate-pulse" />
        ) : isVideo(file) ? (
          <>
            <video src={`${url}#t=0.1`} muted playsInline preload="metadata" onError={onError} className="block w-full" />
            <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-950/60 text-white backdrop-blur-sm">
              <Play className="ml-0.5 h-4 w-4 fill-current" />
            </span>
          </>
        ) : (
          <img src={url} alt={file.caption ?? name} onError={onError} className="block w-full" />
        )}
      </button>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-zinc-950/70 to-transparent px-2.5 pb-2 pt-6 opacity-0 transition-opacity group-hover:opacity-100">
        <span className="min-w-0 truncate text-xs text-white">{file.caption ?? name}</span>
      </div>
      <button
        type="button"
        onClick={() => downloadWorkspaceFile(threadId, file.path)}
        aria-label={`Download ${name}`}
        title="Download"
        className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-md bg-zinc-950/55 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-zinc-950/75 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
      >
        <Download className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function LargeMedia({ threadId, file }: { threadId: string; file: ViewerFile }) {
  const { ref, url, failed, onError } = useFileUrl(threadId, file.path);
  const name = nameOf(file);
  return (
    <div ref={ref as RefObject<HTMLDivElement>} className="flex min-h-40 min-w-40 items-center justify-center" onClick={(e) => e.stopPropagation()}>
      {failed ? (
        <p className="text-sm text-white/60">This file is no longer in the workspace.</p>
      ) : !url ? (
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
      ) : isVideo(file) ? (
        <video src={url} controls autoPlay playsInline onError={onError} className="max-h-[80vh] max-w-[86vw] rounded-md bg-black" />
      ) : (
        <img src={url} alt={file.caption ?? name} onError={onError} className="max-h-[80vh] max-w-[86vw] rounded-md object-contain" />
      )}
    </div>
  );
}

export const MediaOverlay: FC<{
  threadId: string;
  files: ViewerFile[];
  /** The file to open on, or null for the collage. */
  start: number | null;
  onClose: () => void;
}> = ({ threadId, files, start, onClose }) => {
  const [at, setAt] = useState<number | null>(start);
  const set = files.length > 1;

  const step = useCallback((by: number) => {
    setAt((i) => (i === null ? i : (i + by + files.length) % files.length));
  }, [files.length]);
  /** From a tile, back to the collage; from the collage, or a lone file, out. */
  const back = useCallback(() => {
    if (at !== null && set) setAt(null);
    else onClose();
  }, [at, set, onClose]);

  // Focus stays here so the keys work: they go to the workspace's VS Code once its
  // iframe has focus, and it takes focus by itself as it loads.
  const layer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    layer.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') back();
      else if (set && e.key === 'ArrowLeft') step(-1);
      else if (set && e.key === 'ArrowRight') step(1);
    };
    const onBlur = () => setTimeout(() => { if (document.activeElement?.tagName === 'IFRAME') layer.current?.focus(); });
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, [back, step, set]);

  const file = at === null ? null : files[at]!;
  const heading = file ? (file.caption ?? nameOf(file)) : setLabel(files);

  return ReactDOM.createPortal(
    <div
      ref={layer}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      onClick={back}
      className="fixed inset-0 z-[100] flex flex-col bg-zinc-950/90 outline-none"
    >
      <div className="flex shrink-0 items-center gap-3 px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <span className="min-w-0 truncate text-sm text-white/80">{heading}</span>
        {file && set && <span className="shrink-0 text-xs tabular-nums text-white/50">{at! + 1} / {files.length}</span>}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {file && (
            <button type="button" onClick={() => downloadWorkspaceFile(threadId, file.path)} aria-label={`Download ${nameOf(file)}`} title="Download" className={CONTROL}>
              <Download className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close" title="Close" className={CONTROL}>
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {file ? (
        <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6">
          <LargeMedia key={file.path} threadId={threadId} file={file} />
          {set && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" className={`${CONTROL} absolute left-4 top-1/2 -translate-y-1/2`}>
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" className={`${CONTROL} absolute right-4 top-1/2 -translate-y-1/2`}>
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <div className={`mx-auto gap-2 ${collageLayout(files.length)}`} onClick={(e) => e.stopPropagation()}>
            {files.map((f, i) => <CollageTile key={f.path} threadId={threadId} file={f} onOpen={() => setAt(i)} />)}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};
